/**
 * Ground-based radar coverage overlay: site markers + a single dissolved
 * coverage shape (union of every site's radar-horizon circle at the chosen
 * target altitude), using the standard 4/3-earth-radius radar horizon
 * model. MapView calls `addRadarLayers` once on map load and
 * `updateRadarCoverage` whenever the altitude selector / visibility toggle
 * changes, same shape as the rings/coverage functions in layers.ts.
 *
 * Deliberately visually distinct from the existing overlays in layers.ts:
 * amber dashed line = live-data query bbox (COVERAGE_LAYER), slate dashed
 * line = plain distance rings (RINGS_LAYER), violet filled union here =
 * actual ground-radar detection horizon. ARSR vs ASR is distinguished only
 * by marker size — one hue for both, so 193 individually-tinted circles
 * don't wash the map out.
 */
import type { GeoJSONSource, Map as MlMap } from 'maplibre-gl';
import polygonClipping from 'polygon-clipping';
import { circlePolygon } from './geo';
import { RADAR_SITES, type RadarSite } from '../data/radarSites';

export const RADAR_RINGS_SOURCE = 'radar-rings';
export const RADAR_RINGS_FILL_LAYER = 'radar-rings-fill-layer';
export const RADAR_RINGS_LINE_LAYER = 'radar-rings-line-layer';
export const RADAR_SITES_SOURCE = 'radar-sites';
export const RADAR_SITES_LAYER = 'radar-sites-layer';
export const RADAR_SITE_LABELS_LAYER = 'radar-site-labels-layer';
export const RADAR_HOVER_SOURCE = 'radar-hover';
export const RADAR_HOVER_LINE_LAYER = 'radar-hover-line-layer';

const FT_TO_M = 0.3048;
const NM_TO_KM = 1.852;

/** Zoom below which ASR (terminal) sites are hidden — merged coverage + long-range ARSR sites only until zoomed in. */
const ASR_MIN_ZOOM = 6;

/** Fade duration for the on/off toggle, matching layers.ts's TOGGLE_FADE_MS. */
const RADAR_FADE_MS = 220;
const RING_FILL_OPACITY = 0.12;
const RING_LINE_OPACITY = 0.55;
const SITE_OPACITY = 0.9;
const LABEL_OPACITY = 1;
const HOVER_LINE_OPACITY = 0.9;

/**
 * Fallback site elevation (meters MSL) used when a site's real elevation
 * isn't published in the source data (radarSites.ts has none — see its
 * header comment). ARSR-4 long-range sites are disproportionately sited on
 * ridgelines/mountains (Mount Laguna, King Mountain, Bootlegger Ridge, ...)
 * so its fallback is set well above the ASR terminal default, which sites
 * are almost always at/near airport field elevation.
 * ponytail: coarse per-type constant, not per-site real elevation — swap
 * for a DEM/elevation-API lookup if ring accuracy at low altitudes matters.
 */
const ARSR_DEFAULT_ELEV_M = 500;
const ASR_DEFAULT_ELEV_M = 30;

const empty = (): GeoJSON.FeatureCollection => ({ type: 'FeatureCollection', features: [] });

/** Best-effort setPaintProperty — no-ops if the layer isn't ready yet (see layers.ts's identical helper). */
function setPaintOpacity(map: MlMap, layerId: string, prop: Parameters<MlMap['setPaintProperty']>[1], value: number): void {
  if (!map.getLayer(layerId)) return;
  try {
    map.setPaintProperty(layerId, prop, value);
  } catch {
    /* layer not yet ready */
  }
}

/**
 * Radar-horizon range for a target at `targetAltFt`, given a radar sited at
 * `radarElevM` meters MSL, using the standard 4/3-earth-radius model:
 * horizonKm = 4.12 * (sqrt(radarElevM) + sqrt(targetAltM)). Pure function,
 * not clamped to any site's nominal max range — callers (updateRadarCoverage)
 * clamp against `site.maxRangeNm` themselves.
 */
export function horizonRangeKm(radarElevM: number, targetAltFt: number): number {
  const elevM = Math.max(radarElevM, 0);
  const targetAltM = Math.max(targetAltFt, 0) * FT_TO_M;
  return 4.12 * (Math.sqrt(elevM) + Math.sqrt(targetAltM));
}

// Tiny self-check (no test framework wired into client/ for plain .ts
// modules) — fails loudly on import if the formula regresses.
console.assert(horizonRangeKm(0, 0) === 0, 'horizonRangeKm(0,0) should be 0');
console.assert(
  Math.abs(horizonRangeKm(1780, 0) - 4.12 * Math.sqrt(1780)) < 1e-9,
  'horizonRangeKm should reduce to 4.12*sqrt(elevM) at 0ft target altitude',
);
console.assert(
  horizonRangeKm(30, 35000) > horizonRangeKm(30, 10000),
  'horizonRangeKm should increase with target altitude',
);

/** Registers the radar overlay sources/layers, starting fully transparent (hidden) until updateRadarCoverage(..., true) is called. */
export function addRadarLayers(map: MlMap, opts: { projector: boolean }): void {
  if (!map.getSource(RADAR_RINGS_SOURCE)) map.addSource(RADAR_RINGS_SOURCE, { type: 'geojson', data: empty() });
  if (!map.getSource(RADAR_SITES_SOURCE)) map.addSource(RADAR_SITES_SOURCE, { type: 'geojson', data: empty() });
  if (!map.getSource(RADAR_HOVER_SOURCE)) map.addSource(RADAR_HOVER_SOURCE, { type: 'geojson', data: empty() });

  // Single hue for both radar types — ARSR vs ASR is marker size only now.
  const radarColor = opts.projector ? '#c4b5fd' : '#7c3aed';

  if (!map.getLayer(RADAR_RINGS_FILL_LAYER)) {
    map.addLayer({
      id: RADAR_RINGS_FILL_LAYER,
      type: 'fill',
      source: RADAR_RINGS_SOURCE,
      paint: {
        'fill-color': radarColor,
        'fill-opacity': 0,
        'fill-opacity-transition': { duration: RADAR_FADE_MS },
      },
    });
  }
  if (!map.getLayer(RADAR_RINGS_LINE_LAYER)) {
    map.addLayer({
      id: RADAR_RINGS_LINE_LAYER,
      type: 'line',
      source: RADAR_RINGS_SOURCE,
      paint: {
        'line-color': radarColor,
        'line-width': 1.2,
        'line-opacity': 0,
        'line-opacity-transition': { duration: RADAR_FADE_MS },
        'line-dasharray': [1, 2],
      },
    });
  }
  if (!map.getLayer(RADAR_SITES_LAYER)) {
    map.addLayer({
      id: RADAR_SITES_LAYER,
      type: 'circle',
      source: RADAR_SITES_SOURCE,
      paint: {
        'circle-radius': ['match', ['get', 'type'], 'ARSR', 4, 2.6],
        'circle-color': radarColor,
        'circle-stroke-width': 1.2,
        'circle-stroke-color': opts.projector ? '#000000' : '#ffffff',
        'circle-opacity': 0,
        'circle-stroke-opacity': 0,
        'circle-opacity-transition': { duration: RADAR_FADE_MS },
        'circle-stroke-opacity-transition': { duration: RADAR_FADE_MS },
      },
    });
  }
  if (!map.getLayer(RADAR_SITE_LABELS_LAYER)) {
    map.addLayer({
      id: RADAR_SITE_LABELS_LAYER,
      type: 'symbol',
      source: RADAR_SITES_SOURCE,
      minzoom: 7,
      layout: {
        'text-field': ['get', 'label'],
        'text-font': ['Noto Sans Regular'],
        'text-size': opts.projector ? 12 : 10,
        'text-offset': [0, 0.9],
        'text-anchor': 'top',
        'text-optional': true,
      },
      paint: {
        'text-color': opts.projector ? '#e9d5ff' : '#6d28d9',
        'text-halo-color': opts.projector ? '#000000' : '#ffffff',
        'text-halo-width': 1.2,
        'text-opacity': 0,
        'text-opacity-transition': { duration: RADAR_FADE_MS },
      },
    });
  }
  if (!map.getLayer(RADAR_HOVER_LINE_LAYER)) {
    map.addLayer({
      id: RADAR_HOVER_LINE_LAYER,
      type: 'line',
      source: RADAR_HOVER_SOURCE,
      paint: {
        'line-color': radarColor,
        'line-width': 2,
        'line-opacity': HOVER_LINE_OPACITY,
      },
    });
  }

  // Zoom gating: ASR (terminal) sites only appear once zoomed in past
  // ASR_MIN_ZOOM — merged coverage + long-range ARSR sites otherwise.
  // Driven off 'zoom' rather than a filter expression so it degrades to a
  // plain boolean check, no reliance on zoom-in-filter expression support.
  if (!map.getLayer(RADAR_SITES_LAYER)) return;
  let asrVisible: boolean | null = null;
  const applyZoomFilter = () => {
    const show = map.getZoom() >= ASR_MIN_ZOOM;
    if (show === asrVisible) return;
    asrVisible = show;
    map.setFilter(RADAR_SITES_LAYER, show ? null : ['==', ['get', 'type'], 'ARSR']);
  };
  map.on('zoom', applyZoomFilter);
  applyZoomFilter();

  // Hover focus: show the hovered site's own ring at full strength on the
  // dedicated highlight layer, cleared on mouseleave. Self-contained so no
  // MapView wiring is needed.
  let hoveredId: string | null = null;
  map.on('mousemove', RADAR_SITES_LAYER, (e) => {
    if (!radarOn) return;
    const feature = e.features?.[0];
    const id = feature?.properties?.id as string | undefined;
    if (!id || id === hoveredId) return;
    hoveredId = id;
    map.getCanvas().style.cursor = 'pointer';
    const hoverSrc = map.getSource(RADAR_HOVER_SOURCE) as GeoJSONSource | undefined;
    const rangeKm = siteRangeKm.get(id);
    const site = siteById.get(id);
    if (!hoverSrc || rangeKm == null || !site) return;
    hoverSrc.setData({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: [circlePolygon([site.lon, site.lat], rangeKm)] },
          properties: { id: site.id },
        },
      ],
    });
  });
  map.on('mouseleave', RADAR_SITES_LAYER, () => {
    hoveredId = null;
    map.getCanvas().style.cursor = '';
    const hoverSrc = map.getSource(RADAR_HOVER_SOURCE) as GeoJSONSource | undefined;
    hoverSrc?.setData(empty());
  });
}

/** Static lookup for the hover handler — RADAR_SITES doesn't change at runtime. */
const siteById = new Map<string, RadarSite>(RADAR_SITES.map((s) => [s.id, s]));
/** Per-site horizon range at the current target altitude, refreshed on every updateRadarCoverage call — read by the hover handler to draw that site's individual ring. */
const siteRangeKm = new Map<string, number>();

/** Union of every site's coverage circle at a given target altitude, memoized — sites are static so at most one union per distinct altitude (4 in practice) is ever computed. */
const unionCache = new Map<number, GeoJSON.MultiPolygon['coordinates']>();

function coverageUnion(targetAltitudeFt: number): GeoJSON.MultiPolygon['coordinates'] {
  const cached = unionCache.get(targetAltitudeFt);
  if (cached) return cached;

  const polygons: [number, number][][][] = [];
  for (const site of RADAR_SITES) {
    const elevM = site.elevationM ?? (site.type === 'ARSR' ? ARSR_DEFAULT_ELEV_M : ASR_DEFAULT_ELEV_M);
    const rangeKm = Math.min(horizonRangeKm(elevM, targetAltitudeFt), site.maxRangeNm * NM_TO_KM);
    siteRangeKm.set(site.id, rangeKm);
    if (rangeKm <= 0) continue;
    polygons.push([circlePolygon([site.lon, site.lat], rangeKm)]);
  }

  const result = polygons.length > 0 ? polygonClipping.union(polygons[0], ...polygons.slice(1)) : [];
  unionCache.set(targetAltitudeFt, result);
  return result;
}

/** Tracks whether the overlay was visible on the previous call, so toggling fades in/out instead of popping (same pattern as updateRings/updateCoverage in layers.ts). */
let radarOn = false;

const fadeIn = (map: MlMap) => {
  setPaintOpacity(map, RADAR_RINGS_FILL_LAYER, 'fill-opacity', RING_FILL_OPACITY);
  setPaintOpacity(map, RADAR_RINGS_LINE_LAYER, 'line-opacity', RING_LINE_OPACITY);
  setPaintOpacity(map, RADAR_SITES_LAYER, 'circle-opacity', SITE_OPACITY);
  setPaintOpacity(map, RADAR_SITES_LAYER, 'circle-stroke-opacity', SITE_OPACITY);
  setPaintOpacity(map, RADAR_SITE_LABELS_LAYER, 'text-opacity', LABEL_OPACITY);
};

const fadeOut = (map: MlMap) => {
  setPaintOpacity(map, RADAR_RINGS_FILL_LAYER, 'fill-opacity', 0);
  setPaintOpacity(map, RADAR_RINGS_LINE_LAYER, 'line-opacity', 0);
  setPaintOpacity(map, RADAR_SITES_LAYER, 'circle-opacity', 0);
  setPaintOpacity(map, RADAR_SITES_LAYER, 'circle-stroke-opacity', 0);
  setPaintOpacity(map, RADAR_SITE_LABELS_LAYER, 'text-opacity', 0);
  const hoverSrc = map.getSource(RADAR_HOVER_SOURCE) as GeoJSONSource | undefined;
  hoverSrc?.setData(empty());
};

/**
 * Draws the dissolved union of every site's radar-horizon coverage circle
 * for a target flying at `targetAltitudeFt` (one flat fill + one boundary
 * line, not 193 stacked instances) plus the site markers. Pass
 * `visible: false` to fade the overlay out (geometry is left in the
 * source, harmless while transparent, so a later re-enable has something to
 * fade back in from rather than popping).
 */
export function updateRadarCoverage(map: MlMap, targetAltitudeFt: number, visible: boolean): void {
  const ringSrc = map.getSource(RADAR_RINGS_SOURCE) as GeoJSONSource | undefined;
  const siteSrc = map.getSource(RADAR_SITES_SOURCE) as GeoJSONSource | undefined;
  if (!ringSrc || !siteSrc) return;

  if (visible) {
    const coordinates = coverageUnion(targetAltitudeFt);
    const ringFeature: GeoJSON.Feature = {
      type: 'Feature',
      geometry: { type: 'MultiPolygon', coordinates },
      properties: {},
    };
    const siteFeatures: GeoJSON.Feature[] = RADAR_SITES.map((site) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [site.lon, site.lat] },
      properties: { type: site.type, id: site.id, label: site.name },
    }));

    if (!radarOn) {
      fadeOut(map);
      ringSrc.setData({ type: 'FeatureCollection', features: [ringFeature] });
      siteSrc.setData({ type: 'FeatureCollection', features: siteFeatures });
      requestAnimationFrame(() => fadeIn(map));
    } else {
      ringSrc.setData({ type: 'FeatureCollection', features: [ringFeature] });
      siteSrc.setData({ type: 'FeatureCollection', features: siteFeatures });
    }
  } else if (radarOn) {
    fadeOut(map);
  }
  radarOn = visible;
}
